import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { connectedApplicationsResponse, noContent } from "@sitebrew/contracts/v1";

/*
 * Re-exported for the fleet-copied components (`components/user-management`,
 * held byte-for-byte to the archive by `fleet-copy.test.js`), which import
 * their types from here. Nothing is declared here any more.
 */
export type { ConnectedApplication } from "@sitebrew/contracts/v1";
import { keys } from "./query-keys";

/**
 * The applications a person has connected, as the console sees them.
 *
 * **Addressed by the account, not by a workspace**, and that is the resource
 * rather than an omission: a grant lives in `oauthConsent`, which is held by a
 * user, so disconnecting ends that application's access everywhere the person
 * is a member. A workspace in the path would be a segment nothing is looked up
 * by, and a listing that changed with it would be describing a narrower thing
 * than the button underneath it does (`sitebrew-api`, `/v1/connected-applications`).
 */

/*
 * `ConnectedApplication` is `@sitebrew/contracts/v1`'s (#289 row 39). `clientId`
 * is a URL for a client that identified itself by document (CIMD, which is how
 * ChatGPT connects) and an id we issued for one that registered dynamically —
 * unlike `name`, not a value the client chose freely, which is why the screen
 * prints it.
 */

/**
 * What this account has connected.
 *
 * A plain query and not a `DataTable` page, for the reason `useTokens` gives:
 * there is no pager, search or sorting on this screen, and the list is the
 * handful of applications one person connected by clicking *Allow*.
 */
export function useConnectedApplications() {
  return useQuery({
    queryKey: keys.connectedApplications(),
    queryFn: () =>
      api("/connected-applications", connectedApplicationsResponse).then(
        (body) => body.applications,
      ),
  });
}

/**
 * Take one back.
 *
 * `204`, and — unlike a revoked API token — the row **disappears**: withdrawing
 * a grant leaves no row at all, because the consent *is* the access (an access
 * token here is a JWT and is verified without asking the database, so the
 * consent is the only state a person's decision can be recorded in). So the
 * list is invalidated and what comes back is a list without it.
 *
 * The id travels as a **query parameter** because a client id can be a URL —
 * the live connector's is `https://chatgpt.com/oauth/client.json`. As a path
 * segment it would have to be sent as `%2F`, and what reaches the Worker is the
 * path after the edge has normalised the incoming URL, which may decode it.
 * `encodeURIComponent` is still required here: the value contains `/`, `:` and
 * may contain `&`, and an unencoded one would be read as a different parameter.
 */
export function useDisconnectApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId: string) =>
      api(`/connected-applications?client_id=${encodeURIComponent(clientId)}`, noContent, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.connectedApplications() });
    },
  });
}
